/**
 * Copyright © 2026 Adnuntius AS.
 */
import {catchError, defer, map, Observable, of, throwError} from 'rxjs';
import * as _ from 'lodash';

import {ApiConfig} from '../../config/api-config';
import {AdnApiClientService} from './adn-api-client.service';

export type AdminObjectState = 'ACTIVE' | 'INACTIVE' | 'HIDDEN';

export interface SignalAdminResourceModel {
  id: string;
  name?: string;
  objectState?: AdminObjectState;
  validationWarnings?: any[];
  concise?: boolean;
}

export interface SignalAdminResourcePage<T> {
  results: T[];
  totalCount: number;
  page?: number;
}

/** Defaults for option pickers and embedded lists, including bounded id lookups. */
export function resourceOptionParams(input: Record<string, any> | string[] = {}): Record<string, any> {
  let params: Record<string, any> = Array.isArray(input) ? {ids: input} : {...input};
  if (Array.isArray(params.ids)) {
    const ids = params.ids.slice(0, 1001);
    params = {id: ids.join(';'), pageSize: ids.length, ...params};
    delete params.ids;
  }
  return {
    includeHidden: false,
    includeActive: true,
    includeInactive: false,
    ...params,
    orderBy: params.orderBy || 'name',
    pageSize: params.pageSize || 1001
  };
}

export interface SignalAdminResourceListParams {
  includeActive: boolean;
  includeInactive: boolean;
  includeHidden: boolean;
  orderBy: string;
  orderByDirection: 'ASCENDING' | 'DESCENDING';
  page: number;
  pageSize: number;
  concise: boolean;
  locale?: string;
  nullsOrderPolicy?: 'FIRST' | 'LAST';
  [key: string]: any;
}

export interface SignalAdminResourceApi<T extends SignalAdminResourceModel> {
  list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<T>>;
  get(id: string): Observable<T>;
  save(model: T): Observable<T>;
  savePartial(model: T, fields?: string | string[], additionalProps?: Record<string, any>, endpoint?: string): Observable<T>;
  saveMany(models: Partial<T>[]): Observable<void>;
  setObjectState(id: string, objectState: AdminObjectState): Observable<T>;
  setObjectStates(items: Array<{id: string; objectState: AdminObjectState}>): Observable<void>;
  exists(id: string): Observable<boolean>;
}

/** Standard REST contract for signal-native admin resources. */
export abstract class SignalAdminResourceApiBase<T extends SignalAdminResourceModel>
implements SignalAdminResourceApi<T> {
  private readonly uri: string;
  protected readonly apiConfig: ApiConfig;

  protected constructor(
    protected readonly client: AdnApiClientService,
    path: string,
    private readonly defaultParams: Record<string, any> = {},
    private readonly readOnly: () => boolean = () => false
  ) {
    // Hand-built clients in legacy unit tests predate AdnApiClientService's injected config.
    this.apiConfig = client.apiConfig || ApiConfig.obtain();
    this.uri = this.apiConfig.getVersionedUri(path);
  }

  list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<T>> {
    if (Object.prototype.hasOwnProperty.call(params, 'id') && String(params.id ?? '').trim() === '') {
      return of({results: [], totalCount: 0, page: 0});
    }
    return this.client.request<SignalAdminResourcePage<T>>({
      method: 'GET',
      url: this.uri,
      params: {...this.defaultParams, ...params}
    });
  }

  listOptions(input: Record<string, any> | string[] = {}): Observable<SignalAdminResourcePage<T>> {
    const params = resourceOptionParams(input);
    return this.list(params as SignalAdminResourceListParams);
  }

  get(id: string): Observable<T> {
    return this.client.request<T>({
      method: 'GET',
      url: this.itemUri(id),
      params: this.defaultParams
    });
  }

  save(model: T): Observable<T> {
    return this.client.request<T>({
      method: 'POST',
      url: this.itemUri(model.id),
      params: {...this.defaultParams, ...(model.concise === undefined ? {} : {concise: model.concise})},
      body: model
    }).pipe(
      // Create/update endpoints may return only server-owned fields. Match the
      // old resource $save contract by retaining submitted fields that are not
      // repeated in the response.
      map((saved) => ({...model, ...(saved || {})}))
    );
  }

  savePartial(model: T, fields?: string | string[], additionalProps: Record<string, any> = {}, endpoint = ''): Observable<T> {
    return defer(() => {
      if (this.readOnly()) {
        return throwError(() => 'Read-only is the sole option');
      }
      const fieldList = Array.isArray(fields) ? fields : fields ? [fields] : null;
      const selected = fieldList ? _.pick(model, ['id', ...fieldList]) : model;
      const payload = _.cloneDeep({...selected, ...additionalProps}) as T;
      if (!payload.id) {
        return throwError(() => new Error('A resource id is required for a partial save'));
      }
      return this.persistPartial(payload, endpoint === 'save' ? '' : endpoint);
    });
  }

  protected persistPartial(payload: T, endpoint: string): Observable<T> {
    if (endpoint) {
      return throwError(() => new Error('Unsupported partial-save endpoint: ' + endpoint));
    }
    return this.save(payload);
  }

  setObjectState(id: string, objectState: AdminObjectState): Observable<T> {
    return this.client.request<T>({
      method: 'POST',
      url: this.itemUri(id),
      params: this.defaultParams,
      body: {id, objectState}
    });
  }

  setObjectStates(items: Array<{id: string; objectState: AdminObjectState}>): Observable<void> {
    return this.client.request<void>({
      method: 'POST',
      url: this.uri,
      params: this.defaultParams,
      body: items
    });
  }

  saveMany(models: Partial<T>[], params: Record<string, any> = {}): Observable<void> {
    return this.client.request<void>({
      method: 'POST',
      url: this.uri,
      params: {...this.defaultParams, ...params},
      body: models
    });
  }

  exists(id: string): Observable<boolean> {
    return this.client.request<void>({
      method: 'HEAD',
      url: this.itemUri(id),
      params: this.defaultParams,
      ignore404: true
    }).pipe(
      map(() => true),
      catchError((response: any) => response?.status === 404 ? of(false) : throwError(() => response))
    );
  }

  protected resourceUri(path: string | string[]): string {
    return this.apiConfig.getVersionedUri(path);
  }

  private itemUri(id: string): string {
    return this.uri + '/' + encodeURIComponent(id);
  }
}
